import { useEffect, useRef, useState } from 'react'
import { theme } from '../theme'

export type SortDir = 'asc' | 'desc'

export interface FilterChoice {
  value: string
  label: string
}

interface Props {
  label: string
  /** Sort key sent to the API. Omit to make the column unsortable. */
  sortKey?: string
  activeSortKey?: string
  activeSortDir?: SortDir
  onSort?: (key: string, dir: SortDir) => void
  /** Multi-select choices. Omit for a column with no value filter. */
  choices?: FilterChoice[]
  selected?: string[]
  onSelect?: (values: string[]) => void
  /**
   * Free-text glob mode instead of a value list — for high-cardinality columns
   * (the run token) where enumerating every value would be useless.
   */
  globValue?: string
  onGlob?: (value: string) => void
  globPlaceholder?: string
}

/**
 * Table column header carrying its own sort toggle and filter control.
 *
 * Sorting and filtering are applied SERVER-side by the caller: the table only
 * ever holds one page, so sorting locally would reorder that page rather than
 * the whole result set, and filtering locally would hide rows without pulling
 * matching rows from later pages.
 */
export function ColumnHeader({
  label,
  sortKey,
  activeSortKey,
  activeSortDir,
  onSort,
  choices,
  selected = [],
  onSelect,
  globValue,
  onGlob,
  globPlaceholder,
}: Props) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const hasFilter = Boolean(choices?.length) || Boolean(onGlob)
  const isSorted = Boolean(sortKey) && activeSortKey === sortKey
  const filterActive = selected.length > 0 || Boolean(globValue)

  // Close on outside click / Escape so the panel never strands over the table.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggleSort = () => {
    if (!sortKey || !onSort) return
    // First click on a new column sorts descending (most recent / largest
    // first, which is what you almost always want); clicking the active
    // column flips direction.
    onSort(sortKey, isSorted && activeSortDir === 'desc' ? 'asc' : 'desc')
  }

  const toggleChoice = (value: string) => {
    if (!onSelect) return
    onSelect(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
      <span
        onClick={toggleSort}
        role={sortKey ? 'button' : undefined}
        tabIndex={sortKey ? 0 : undefined}
        onKeyDown={e => {
          if (sortKey && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            toggleSort()
          }
        }}
        title={sortKey ? `Sort by ${label}` : undefined}
        style={{
          cursor: sortKey ? 'pointer' : 'default',
          color: isSorted ? theme.text : undefined,
          fontWeight: isSorted ? 600 : undefined,
          userSelect: 'none',
        }}
      >
        {label}
        {isSorted && <span style={{ marginLeft: 4 }}>{activeSortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>

      {hasFilter && (
        <button
          onClick={() => setOpen(o => !o)}
          aria-label={`Filter by ${label}`}
          title={`Filter by ${label}`}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: '0 2px',
            fontSize: '0.7rem',
            lineHeight: 1,
            color: filterActive ? theme.primary : theme.textMuted,
            fontWeight: filterActive ? 700 : 400,
          }}
        >
          {filterActive ? '⏷●' : '⏷'}
        </button>
      )}

      {open && hasFilter && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            minWidth: 210,
            maxHeight: 320,
            overflowY: 'auto',
            background: theme.bgElevated,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.55)',
            zIndex: 30,
            padding: '0.5rem',
            fontWeight: 400,
            textTransform: 'none',
          }}
        >
          {onGlob ? (
            <>
              <input
                autoFocus
                value={globValue || ''}
                onChange={e => onGlob(e.target.value)}
                placeholder={globPlaceholder || 'e.g. *KT74 or E2E-BWB*'}
                style={{
                  width: '100%',
                  padding: '0.35rem 0.4rem',
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  boxSizing: 'border-box',
                }}
              />
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.68rem', color: theme.textMuted, lineHeight: 1.4 }}>
                <code>*</code> matches any run of characters, <code>?</code> exactly one.
                Plain text matches anywhere. Case-insensitive.
              </p>
              {globValue && (
                <button onClick={() => onGlob('')} style={clearBtn}>Clear</button>
              )}
            </>
          ) : (
            <>
              {choices!.map(c => (
                <label
                  key={c.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    padding: '0.2rem 0',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(c.value)}
                    onChange={() => toggleChoice(c.value)}
                  />
                  <span>{c.label}</span>
                </label>
              ))}
              {choices!.length === 0 && (
                <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>No values yet</span>
              )}
              {selected.length > 0 && (
                <button onClick={() => onSelect?.([])} style={clearBtn}>Clear ({selected.length})</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

const clearBtn: React.CSSProperties = {
  marginTop: '0.5rem',
  width: '100%',
  padding: '0.3rem',
  fontSize: '0.75rem',
  background: theme.bgAlt,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  cursor: 'pointer',
}

export default ColumnHeader
